/**
 * Main controller for a chat widget iframe.
 * Dependencies: chatSession, chatWidget, socket.io
 */
import SuiEventEmitter from '../tools/SuiEventEmitter.js';
import nunjucks from 'nunjucks/browser/nunjucks-slim.js';
import ChatMessage from './models/ChatMessage.js';
import { adjustStatusRequestForCoverage, adjustFallbacksForCoverage } from './widget/coopUtil.js';
import { BotController } from '../chatbot/BotController.js';
import StatusResponse from './StatusResponse.js';
import BotService from '../chatbot/BotService.js';
import SuiStorage from '../tools/SuiStorage.js';
import opSession from './models/OperatorSession.js';
import SuiNotify from '../tools/SuiNotify.mjs';
import autoView from './views/chatWidgetAutoView.js';
import authView from './views/chatWidgetAuthView.js';
import offlineView from './views/chatWidgetOfflineView.js';
import loginView from './views/chatWidgetLoginView.js';
import fileView from './views/chatWidgetFileView.js';
import chatView from './views/chatWidgetChatView.js';
import qformView from './views/chatWidgetQFormView.js';
import bformView from './views/chatWidgetBookingFormView.js';
import { jqGetAjaxError } from '../tools/requestUtil.js';

const chatWidgetControl = {
    /**
     * @prop {Object} model A chatWidget model
     */
    model: null,
    /**
     * @prop {String} socketIoUrl URL for socket.io script.
     */
    socketIoUrl: null,
    /**
     * @prop {String} cascadeServer URL of cascade server.
     */
    cascadeServer: '',
    /**
     * @prop {String} cascadeServer URL of local domain for proxying to chat server.
     */
    altCascadeServer: '',
    /**
     * @prop {Object} cascade Socket.io object connection to chat server
     */
    cascade: null,
    /**
     * @prop {SuiEventEmitter} eventEmitter instantiation of such
     */
    eventEmitter: null,
    /**
     * @prop {Object} nunjucks Customized nunjucks environment
     */
    nunjucks: null,
    /**
     * @prop {Object} patronSession A patronSession object.
     */
    patronSession: null,
    /**
     * @prop {String} resumeToken Token issued by Cascade to prove ownership of a session on reconnect/peel.
     */
    resumeToken: '',
    /**
     * Usually just 1 in this array, but more if there are transfers.
     * @prop {Array} operatorsList opSession objects for ops in the chat
     */
    operatorsList: [],
    /**
     * @prop {Object} views Bunch of view objects.
     */
    views: {},
    /**
     * @prop {Object} departments Department id => DeptObj
     */
    departments: {},
    /**
     * @prop {BotController|null} botController
     */
    botController: null,
    /**
     * @prop {Function} typing_end_timeout Is typing... timeout.
     */
    typing_end_timeout: null,
    /**
     * @prop {Boolean} is_typing_currently Are in the process of telling the op we are typing.
     */
    is_typing_currently: false,
    /**
     * @prop {Number[]} disconnects Timestamps for disconnects so we can track them over time.
     */
    disconnects: [],
    /**
     * @prop {Number} disconnectTimeThreshold Seconds threshold within wichi the disconnect count will trigger an alternate connection.
     */
    disconnectTimeThreshold: 15,
    /**
     * @prop {Number} disconnectCountThreshold Disconnects that must happen within the seconds threshold above to trigger an alternate connection.
     */
    disconnectCountThreshold: 2,
    /**
     * @prop {String[]} transports Chat transports for socket.io
     * Use websockets by default.
     * If there are connection issues, we add polling in for the second go around
     * with the proxied local domain.
     */
    transports: ['websocket'],
    /**
     * @prop {String} captchaPayload Verified captcha payload to send when starting a session.
     */
    captchaPayload: '',
    /**
     * @prop {Boolean} captchaPending Waiting on the captcha in the chat pane before starting a session.
     */
    captchaPending: false,
    /**
     * @prop {Boolean} sessionStarting A start session request is in progress.
     */
    sessionStarting: false,
    /**
     * Save the session id into localStorage for widget use
     */
    saveSessionId: function () {
        const sessionId = this.patronSession.get('sessionId');
        if (sessionId === '') {
            return;
        }
        const obj = {
            sessionId: sessionId,
            date: Math.floor(Date.now() / 1000),
            iid: Number(this.patronSession.get('iid')),
            widgetId: this.model.get('id'),
            url: this.patronSession.get('referer'),
            resumeToken: this.resumeToken || '',
        };
        SuiStorage.set('libchat_session', JSON.stringify(obj));
    },
    /**
     * Retrieve a saved session id (if found)
     * will also delete a session id that is too old
     * this allows for same page reloads to reconnect.
     * @return {String} sessiond id or empty string
     */
    getSessionId: function () {
        let obj = SuiStorage.get('libchat_session');
        if (obj === '') {
            return '';
        }
        try {
            obj = JSON.parse(obj);
            if (!obj.sessionId || obj.sessionId === '') {
                this.deleteSessionId();
                return '';
            }
            const cur_time = Math.floor(Date.now() / 1000);
            // compare to obj.date, if it's more than 2 minutes then remove it
            // also delete for different IIDs
            if ((cur_time - obj.date) > (60 * 2) || (obj.iid !== Number(this.model.get('iid')))) {
                this.deleteSessionId();
                return '';
            }
            // check for widget/refer match
            if (obj.widgetId !== this.model.get('id')
                || obj.url.replace('&amp;', '&') !== this.patronSession.get('referer').replace('&amp;', '&')
            ) {
                // don't delete in case they do get back to their widget.
                return '';
            }
            // should we update the stored timestamp here?
            this.resumeToken = obj.resumeToken || '';
            return obj.sessionId;
        } catch {
            this.deleteSessionId();
        }
        return '';
    },
    /**
     * Read the saved session from localStorage without validating or deleting it.
     * @return {Object} Saved session data, or an empty object.
     */
    peekStoredSession: function () {
        try {
            const obj = JSON.parse(SuiStorage.get('libchat_session'));
            return (obj && typeof obj === 'object') ? obj : {};
        } catch {
            return {};
        }
    },
    /**
     * Delete the saved sessiond id (at end of chat)
     */
    deleteSessionId: function () {
        this.resumeToken = '';
        SuiStorage.remove('libchat_session');
    },
    /**
     * Set the resume token and keep the live socket.io connection's query
     * string in sync with it.
     *
     * `setupSocket()` only bakes `token` into `this.cascade.io.opts.query`
     * once, at connection time - for a brand new session that's before any
     * token exists. Socket.io's own automatic reconnection (e.g. after a
     * brief network drop, handled internally rather than via a fresh
     * `setupSocket()` call) reuses that same query string
     * (`handleCascadeDisconnect` only appends `&reconnect=true` to it), so
     * without this it would keep resending an empty/stale token forever
     * once one exists, and Cascade would reject those reconnects.
     * @param {String} token Resume token issued by Cascade.
     */
    setResumeToken: function (token) {
        if (!token) {
            return;
        }
        this.resumeToken = token;

        if (!this.cascade || !this.cascade.io || !this.cascade.io.opts) {
            return;
        }
        const query = this.cascade.io.opts.query || '';
        const tokenParam = `token=${encodeURIComponent(token)}`;
        this.cascade.io.opts.query = (/(^|&)token=[^&]*/).test(query)
            ? query.replace(/(^|&)token=[^&]*/, `$1${tokenParam}`)
            : (query ? `${query}&${tokenParam}` : tokenParam);
    },
    /**
     * Post message to iframe parent.
     * @param {String} action Action param we want to send.
     * @param {Object} options Other options to pass through.
     */
    postWindowMessage: function (action, options) {
        const payload = {
            libchat_widget: this.model.get('id'),
            action: action,
        };
        if (typeof options !== 'undefined') {
            const optKeys = Object.keys(options);
            optKeys.forEach((opt) => {
                payload[opt] = options[opt];
            });
        }
        window.parent.postMessage(payload, '*');
    },
    /**
     * Adjust height of widget body
     * Related to issues with iframe embeddeding and using full height for chat pane flexbox
     */
    adjustWidgetHeight: function () {
        const height = window.innerHeight;
        if (height > 0) {
            document.body.style.height = `${window.innerHeight}px`;
        }
    },
    /**
     * Find first selectable element in parent
     * @param {DOMElement} parent Parent element to look in.
     * @return {DOMElement}
     */
    findFirstFocusable: function (parent) {
        if (typeof parent === 'undefined') {
            parent = document;
        }
        return parent.querySelector('input, textarea, select, a, button, [tabindex]');
    },
    /**
     * Close a widget from the inside
     */
    closeChat: function () {
        // set the autopop as denied, else sometimes it can immediately pop back up again
        // if the widget is reloaded.
        this.saveAutoPopDeny();
        this.postWindowMessage('closeWidget');
    },
    /**
     * Change the current view.
     * @param {String} viewName View's name on the this.views prop
     */
    changeViewTo: function (viewName) {
        if (!this.views[viewName]) {
            return;
        }
        document.querySelectorAll('.chatpane').forEach((el) => el.classList.add('hidden'));
        this.views[viewName].showView();
        this.adjustWidgetHeight();
    },
    /**
     * reload the widget
     */
    reloadWidget: function () {
        this.deleteSessionId();
        // need to remove any peelKey param
        const url = new URL(window.location);
        url.searchParams.delete('peelKey');
        window.location = url;
    },
    /**
     * Render the libauth overlay.
     */
    renderLibAuthOverlay: function () {
        // If there is no auth or patron is validated we can stop now.
        if (this.model.isAuthValidOrNotSet()) {
            return;
        }
        // Otherwise make the overlay show up
        // This will also disable actions/focus on whatever pane it is overlaying (login or chat)
        this.views.libauth = Object.create(authView);
        const config = {
            model: this.model,
            patronSession: this.patronSession,
            templateEng: this.nunjucks,
        };
        this.views.libauth.initialize(config);
        this.views.libauth.render();
    },
    /**
     * Handler for denying the autopop widget.
     * Tell parent widget to close the widget.
     */
    denyAutopopRequest: function () {
        // close widget
        this.postWindowMessage('closeWidget');
        this.saveAutoPopDeny();
    },
    /**
     * Handler for accepting autopop widget.
     */
    acceptAutopopRequest: function () {
        this.postWindowMessage('chatStarted');

        if (!this.model.isOnline()) {
            if (this.botController.isBotActiveOffline()) {
                this.renderChatView();
                if (this.model.isAuthValidOrNotSet()) {
                    this.botController.startBot(false);
                }
            }
            return;
        }

        // don't show login for online active bot
        if (this.model.get('skip_login') === false
            && !this.botController.isBotActiveOnline()) {
            this.renderLogin();
        } else {
            this.renderChatView();
            if (this.botController.isBotActiveOnline()
                && this.model.isAuthValidOrNotSet()) {
                this.botController.startBot(true);
            }
        }
    },
    /**
     * If slide widget was autoloaded we see a different pane
     * @param {Boolean} online Is chat online
     */
    renderAutoload: function (online) {
        if (!this.views.auto) {
            this.views.auto = Object.create(autoView);

            // is bot active, if so pass in the bot image name...
            // these props are redundant because of later use in the view
            const bot = {
                botname: '',
                botimage: '',
            };
            if ((online && this.botController.isBotActiveOnline())
                || (!online && this.botController.isBotActiveOffline())) {
                bot.botname = this.botController.flow.getName();
                bot.botimage = this.botController.flow.profile_url;
            }

            const config = {
                model: this.model,
                events: {
                    'click #s-lch-autoload-yes': this.acceptAutopopRequest.bind(this),
                    'click #s-lch-autoload-no': this.denyAutopopRequest.bind(this),
                },
                templateEng: this.nunjucks,
            };

            this.views.auto.initialize(config);
            this.views.auto.render(bot);
        }

        // Don't reload the view if it's already showing.
        // This helps when visibilitychange triggers a recheck of status
        // so the widget doesn't pop open everytime you look back at the page.
        if (this.views.auto.isShowing()) {
            return;
        }
        this.changeViewTo('auto');

        this.postWindowMessage('autopop');

        // for unknown reasons can't get accurate size of pane
        // until the iframe is open.
        // so we have a miniscule delay and then recalc height.
        // for other unknown reasons IE demands that I have this equation all broken out
        setTimeout(() => {
            let height = document.getElementById('autoload').scrollHeight;
            const padding = parseInt(document.body.style.paddingTop, 10);
            height += padding * 3;
            this.postWindowMessage('height', { height: height });
        }, 100);
    },
    /**
     * Make sure online status is all cleared
     * Necessary for the status recheck so an old one doesn't stick around.
     */
    resetOnlineStatus: function () {
        this.model.set('online_rule', false);
        this.patronSession.set({
            uid: 0,
            did: 0,
            coopId: '',
        });
    },
    /**
     * Set patron's data from online rule.
     * @param {StatusResponse} rule Current online rule.
     */
    setPatronDataFromOnlineRule: function (rule) {
        // Set default uid/did on patronSession
        // helpful for skip_login === true
        const onlineDept = rule.getFirstDept();
        const props = {
            uid: rule.opId,
            did: (onlineDept.id) ? onlineDept.id : 0,
            coopId: '',
        };
        // if op and dept were offline, then coop is possible.
        if (props.uid === 0 && props.did === 0) {
            props.coopId = rule.coopId;
        }
        this.patronSession.set(props);
    },
    /**
     * Success Callback for online status request
     * @param {Object} data Chat status data from cascade.
     */
    handleStatusCheck: function (data) {
        const response = new StatusResponse(data, this.departments);
        if (response.isOffline) {
            this.resetOnlineStatus();
            const botActiveOffline = this.botController.isBotActiveOffline();
            if (botActiveOffline && this.model.get('autopop')) {
                this.renderAutoload(false);
                return;
            }
            this.renderOffline();
            let online = false;
            if (botActiveOffline) {
                online = true;
            }
            this.postWindowMessage('changeStatus', { online });
            return;
        }
        this.model.set('online_rule', response);

        // Set default uid/did on patronSession
        this.setPatronDataFromOnlineRule(response);

        this.postWindowMessage('changeStatus', { online: true });
        this.startWidget();
    },
    /**
     * Check for online status of widget and show appropriate pane
     */
    getOnlineStatus: function () {
        const adjustedRules = adjustStatusRequestForCoverage(this.model.get('onlineRules'));
        window.$.ajax({
            url: `${this.cascadeServer}/widget_status`,
            dataType: 'json',
            data: {
                iid: this.model.get('iid'),
                rules: JSON.stringify(adjustedRules),
            },
            success: this.handleStatusCheck.bind(this),
            timeout: 10000,
        }).fail(() => {
            this.resetOnlineStatus();
            this.renderOffline();
        });
    },
    /**
     * Show the offline template
     */
    renderOffline: function () {
        // Check for offline bot...
        if (this.botController.isBotActiveOffline()) {
            // Render chat view, bot will start then.
            this.renderChatView();
            if (this.model.isAuthValidOrNotSet()) {
                this.botController.startBot(false);
            }
            // expand float-y widget
            this.postWindowMessage('expandWidget');
            return;
        }

        if (!this.views.offline) {
            this.views.offline = Object.create(offlineView);
            const config = {
                model: this.model,
                patronSession: this.patronSession,
                emitter: this.eventEmitter,
                templateEng: this.nunjucks,
            };

            this.views.offline.initialize(config);
            this.views.offline.render();
        }
        this.changeViewTo('offline');

        // expand float-y widget
        this.postWindowMessage('expandWidget');

        // just in case
        this.deleteSessionId();
    },

    /**
     * Upload images from the message box
     */
    uploadFiles: async function () {
        const formData = new FormData();
        formData.append('iid', this.patronSession.iid);

        this.views.chat.filesToUpload.forEach((item, index) => {
            formData.append('ufile', item.file, `image-${index}.png`);
        });

        try {
            const response = await fetch(`/chat/session/${this.patronSession.getChannel()}/file`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                if (data.error) {
                    console.error(data.error);
                }
                const message = document.createElement('span');
                message.innerText = '[image]: ';
                const fileLink = document.createElement('a');
                fileLink.href = data.url;
                fileLink.setAttribute('target', '_blank');
                fileLink.classList.add('s-la-cfile');
                fileLink.innerText = data.url;
                fileLink.dataset.preview = 'inline';
                message.appendChild(fileLink);
                return `\n${message.outerHTML}`;
            }
        } catch (error) {
            console.error(error);
        }
    },

    /**
     * Handle a message submitted from the chat view.
     * @param {String} message Message text.
     */
    handleMessageSubmit: function (message) {
        if (message === '' && this.views.chat.filesToUpload.length < 1) {
            return;
        }
        // Ignore messages while waiting on the captcha or the request to start the session.
        if (this.cascade === null && (this.captchaPending || this.sessionStarting)) {
            return;
        }

        if (this.cascade === null) {
            // starting a chat with this message
            this.patronSession.set('client_question', message);
            this.model.set('client_question', message);
            this.views.chat.clearTextarea();
            // show contact request?
            if (this.patronSession.get('client_contact') === ''
                && this.model.get('contact_request')
                && this.model.get('contact_required')) {
                this.eventEmitter.trigger('requestContact');
                return;
            }
            this.startSession();
            return;
        }

        // If follow-up message was shown then allow ticket creation.
        if (document.getElementById('s-lch-msg-followup')) {
            if (this.patronSession.get('client_contact') === '') {
                // show contact form, leaving this message in the form for resubmission
                this.eventEmitter.trigger('requestContact');
                return;
            }
            this.createTicket(message);
            return;
        }

        // If offline/not chatting right now...
        if (this.cascade.disconnected || !this.model.get('chatting')) {
            this.eventEmitter.trigger('chatMsgNotSendable');
            return;
        }

        this.postChatMsg(message);
    },
    /**
     * Handle a successful user uploaded file. Called via file view on upload success
     * @param {String} fileMessage Message to emit and show.
     */
    handleFileUpload: function (fileMessage) {
        const obj = {
            room: this.patronSession.getRoom(),
            message: fileMessage,
        };
        this.cascade.emit('newMessage', obj);
        this.eventEmitter.trigger('changeView', 'chat');
        this.views.chat.showFileUpload(fileMessage);
    },
    /**
     * Render the login form
     */
    renderLogin: function () {
        if (!this.views.login) {
            this.views.login = Object.create(loginView);
            const config = {
                model: this.model,
                patronSession: this.patronSession,
                templateEng: this.nunjucks,
            };
            this.views.login.initialize(config);
        }
        this.views.login.render();
        this.changeViewTo('login');

        // Render libauth overlay if necessary
        this.renderLibAuthOverlay();
    },
    /**
     * Render the chatting pane.
     */
    renderChatView: function () {
        if (!this.views.chat) {
            this.views.chat = Object.create(chatView);
            const config = {
                model: this.model,
                events: {
                    'click .s-lch-btn-file': this.renderFileView.bind(this),
                    'click .s-lch-btn-peel': this.peelOut.bind(this),
                    'click .s-lch-btn-end': this.endChat.bind(this),
                    'click .s-lch-btn-close': this.closeChat.bind(this),
                },
                patronSession: this.patronSession,
                emitter: this.eventEmitter,
                operatorsList: this.operatorsList,
                templateEng: this.nunjucks,
            };

            this.views.chat.initialize(config);
            this.views.chat.render();
        }
        this.changeViewTo('chat');

        // Render libauth overlay if necessary
        this.renderLibAuthOverlay();
    },
    /**
     * Render the file upload pane.
     */
    renderFileView: function () {
        if (!this.views.file) {
            this.views.file = Object.create(fileView);
            const config = {
                model: this.model,
                patronSession: this.patronSession,
                emitter: this.eventEmitter,
                templateEng: this.nunjucks,
            };

            this.views.file.initialize(config);
            this.views.file.render();
        }
        this.changeViewTo('file');
    },
    /**
     * Render the question form view for chatbot
     * @param {String} html Form html
     * @param {Object} errormsg Localized form error messages
     * @param {Object} translations Localized text for form
     * @param {Object} autoReplySettings Autoreply on/off/hours/etc.
     * @param {TicketAction} action Bot ticket action
     * @param {Array<Object>} rules Field rules
     */
    renderQuestionFormView: function ({ html, errormsg, translations, autoReplySettings, action, rules }) {
        if (!this.views.qform) {
            this.views.qform = Object.create(qformView);
            const config = {
                model: this.model,
                patronSession: this.patronSession,
                emitter: this.eventEmitter,
            };
            this.views.qform.initialize(config);
        }
        // any render of this view completely resets it
        this.views.qform.render({
            errormsg,
            translations,
            html,
            autoReplySettings,
            action,
            rules,
        });

        this.changeViewTo('qform');
    },
    /**
     * Render the booking form view for chatbot
     * @param {String} html Form html
     * @param {LibCalAction} action Bot ticket action
     */
    renderBookingFormView: function ({ html, action }) {
        if (!this.views.bform) {
            this.views.bform = Object.create(bformView);
            const config = {
                model: this.model,
                patronSession: this.patronSession,
                emitter: this.eventEmitter,
            };
            this.views.bform.initialize(config);
        }
        // any render of this view completely resets it
        this.views.bform.render({
            html,
            action,
        });

        this.changeViewTo('bform');
    },
    /**
     * Start up the widget now that it is rendered and we know the status
     */
    startWidget: function () {
        // Did we get here from peeling out? Server side set the peel prop (and resume token)
        // on patronSession when the widget loaded. This takes precedence over localStorage,
        // which isn't shared with the original widget when it's embedded on another site
        // (browsers partition third-party iframe storage).
        let ex_sessionId = this.patronSession.get('peel');
        const peelToken = this.patronSession.get('peelToken') || '';
        // Keep the token off the session model so toJSON() never sends it to cascade/LA.
        delete this.patronSession.peelToken;
        if (ex_sessionId !== '') {
            if (peelToken !== '') {
                this.resumeToken = peelToken;
            } else {
                // No token handed over (older widget js), only use one stored for this same session.
                const stored = this.peekStoredSession();
                this.resumeToken = stored.sessionId === ex_sessionId ? (stored.resumeToken || '') : '';
            }
        } else {
            // check for existing session id from localStorage
            // this could be reloading the page.
            ex_sessionId = this.getSessionId();
        }
        // if so just connect and return to the chat
        if (ex_sessionId !== '') {
            this.startPeel(ex_sessionId);
            return;
        }

        // If this was triggered by the auto-popup feature?
        if (this.model.get('autopop') && !this.model.isEmbed() && !this.model.isButton()) {
            // check for already denied autopop
            if (this.autoPopDenied() === false) {
                this.renderAutoload(true);
                return;
            }
        }

        // expand float widget
        this.postWindowMessage('expandWidget');

        // don't show login for online active bot
        if (this.model.get('skip_login') === false
            && !this.botController.isBotActiveOnline()) {
            this.renderLogin();
        } else {
            this.renderChatView();
            // stop here is bot started up.
            if (this.botController.isBotActiveOnline()) {
                if (this.model.isAuthValidOrNotSet()) {
                    this.botController.startBot(true);
                }
                return;
            }
            // show away status and message.
            if (this.model.get('online_rule').away) {
                this.eventEmitter.trigger('chatAway');
                return;
            }
        }
    },
    /**
     * Start up a peeled out session
     * instead of startSession() (which is used for regular chat start)
     * @param {String} ex_sessionId Session id that was stored locally for peelout
     */
    startPeel: function (ex_sessionId) {
        this.patronSession.set('sessionId', ex_sessionId);
        this.patronSession.set('peel', ex_sessionId);
        // Set peel so widget model knows we peeled out too.
        this.model.set('peel', ex_sessionId);
        this.loadSocketIo();
        this.postWindowMessage('expandWidget');
    },
    /**
     * Callback: Start session success.
     * @param {Object} data Patron session data (some new or transformed from what was in patronSession)
     */
    startSessionSuccess: function (data) {
        this.sessionStarting = false;
        this.captchaPayload = '';
        this.captchaPending = false;
        this.model.emitter.trigger('captcha:done');
        this.patronSession.set(data.session);
        // Adjust the fallbacks to account for the current time
        // So coop coverage times are accounted for.
        const fallbacks = this.patronSession.get('fallbacks');
        if (fallbacks) {
            // strip out non-coop ones and ones without seconds
            const adjustedFallbacks = adjustFallbacksForCoverage(fallbacks, this.model.get('timedFallback'));
            this.patronSession.set('fallbacks', adjustedFallbacks);
        }
        this.loadSocketIo();
    },
    /**
     * Callback: Start session error.
     * Also start bot session error.
     * @param {Object} jqxhr Jquery xhr object.
     */
    startSessionError: function (jqxhr) {
        this.sessionStarting = false;
        const error = jqGetAjaxError(jqxhr);
        // HttpConstants::STATUS_CAPTCHA_ERROR, patron can retry the captcha.
        if (jqxhr && jqxhr.status === 449) {
            this.handleCaptchaError(error);
            return;
        }
        let event = 'chatError';
        // HttpConstants::STATUS_FORBIDDEN for banned/blocked
        if (jqxhr && jqxhr.status === 403) {
            event = 'chatBlockedError';
        }
        this.model.emitter.trigger(event, error);
    },
    /**
     * Validate the session data before we start...
     */
    validateSession: function () {
        // make sure there is a name
        if (this.patronSession.get('name') === '') {
            this.patronSession.set('name', this.model.generateAnonName());
        }
    },
    /**
     * Start up a session, send ajax, which puts the chat data in the database.
     */
    startSession: function () {
        // Only one start request at a time, a duplicate would create another chat thread.
        if (this.sessionStarting) {
            return;
        }
        if (this.model.get('use_captcha') && this.captchaPayload === '') {
            // No form captcha was verified (skip login, bot handoff), so ask for it in the chat pane.
            this.captchaPending = true;
            this.model.emitter.trigger('captcha:required');
            return;
        }
        this.validateSession();
        const info = this.patronSession.toJSON();
        info.key = this.model.get('key');
        info.widget_id = this.model.get('id');
        info.unit_id = this.model.get('unit_id');
        // This may be the default widget auth id
        // but it may be one passed in via an LA public page.
        info.auth_id = this.model.get('auth_id');
        if (this.model.get('use_captcha')) {
            info.altcha = this.captchaPayload;
            // Server only accepts a payload once.
            this.captchaPayload = '';
        }
        this.sessionStarting = true;

        /**
         * This saves a ChatThread object to the database and sets up a few properties
         * such as the session id, ip address, user agent, etc.
         * It also runs user string input through HTMLPurifier
         * and check the IP ban list.
         */
        window.$.ajax({
            url: '/chat/session',
            method: 'POST',
            data: info,
        })
            .done(this.startSessionSuccess.bind(this))
            .fail(this.startSessionError.bind(this));
    },
    /**
     * Handler: captcha verified (or cleared when given an empty payload).
     * @param {String} payload Altcha payload.
     */
    handleCaptchaVerified: function (payload = '') {
        this.captchaPayload = payload;
        if (payload !== '' && this.captchaPending) {
            this.captchaPending = false;
            this.startSession();
        }
    },
    /**
     * Captcha was rejected by the server.
     * The payload can't be reused so the patron has to verify again.
     * @param {String} error Error message.
     */
    handleCaptchaError: function (error) {
        this.captchaPayload = '';
        // Site may have turned on captcha after this widget loaded.
        this.model.set('use_captcha', true, { silent: true });
        if (this.model.get('loggedIn')) {
            // Login form, allow it to be resubmitted.
            this.model.set('loggedIn', false, { silent: true });
            this.model.emitter.trigger('captcha:failed', error);
            return;
        }
        this.captchaPending = true;
        this.model.emitter.trigger('captcha:required', error);
    },
    /**
     * Call the server and trigger a new bot session in the db.
     * @param {Number} nextActionId Action to trigger after session is init'd.
     * @param {String} flowUuid Flow UUID
     */
    startBotSession: function ({ nextActionId = 0, flowUuid = '' }) {
        /**
         * This saves a BotSession object to the database and sets up a few properties
         * such as the session id
         * It also checks the IP ban list.
         */
        window.$.ajax({
            url: '/chat/bot/session',
            method: 'POST',
            data: {
                widget_id: this.model.get('id'),
                // This may be the default widget auth id
                // but it may be one passed in via an LA public page.
                auth_id: this.model.get('auth_id'),
                referer: this.patronSession.get('referer'),
                client_contact: this.patronSession.get('client_contact'), // via LibAuth, sometimes
                name: this.patronSession.get('name'), // via LibAuth, sometimes
            },
        })
            .done(({ session = {}, flow = {} }) => {
                this.patronSession.set({
                    sessionId: session.sessionId || '',
                    flowUuid,
                });
                this.eventEmitter.trigger('bot:loadactions', { actionId: nextActionId, flow });
            })
            .fail(this.startSessionError.bind(this)); // Note: reusing regular session callback
    },
    /**
     * Bot tries to send patron to chat.
     * @param {StatusRule} rule Status rule to check
     * @param {Number} actionId Current action id
     * @param {Boolean} fallbackAttempt Should we try the normal widget rules instead.
     */
    sendBotToChat: function ({ rule = null, actionId, fallbackAttempt = false }) {
        if (!rule && !fallbackAttempt) {
            this.eventEmitter.trigger('bot:chatfail', { actionId });
            return;
        }
        const adjustedRules = fallbackAttempt
            ? adjustStatusRequestForCoverage(this.model.get('onlineRules'))
            : adjustStatusRequestForCoverage([rule]);
        if (adjustedRules.length === 0) {
            this.eventEmitter.trigger('bot:chatfail', { actionId });
            return;
        }
        window.$.ajax({
            method: 'GET',
            url: `${this.cascadeServer}/widget_status`,
            dataType: 'json',
            data: {
                iid: this.model.get('iid'),
                rules: JSON.stringify(adjustedRules),
            },
            timeout: 10000,
        })
            .done((data) => {
                const response = new StatusResponse(data, this.departments);
                if (response.isOffline) {
                    this.resetOnlineStatus();
                    this.eventEmitter.trigger('bot:chatoffline', { actionId, fallbackAttempt });
                    return;
                }
                this.model.set('online_rule', response);

                // Set default uid/did on patronSession
                this.setPatronDataFromOnlineRule(response);

                this.postWindowMessage('changeStatus', { online: true });
                this.startSession();
            })
            .fail(() => {
                this.resetOnlineStatus();
                this.eventEmitter.trigger('bot:chatfail', { actionId });
            });
    },
    /**
     * Handle an operator leaving. They may or may not be in the chat.
     * @param {Object} operator Data about the operator
     * @param {Boolean} purposeful Was this an op purposefully leaving this chat.
     * @param {Boolean} endchat Was the chat purposefully ended by the op.
     */
    removeOperator: function (operator, purposeful, endchat) {
        if (!operator.opId || operator.role !== 'op' || this.operatorsList.length === 0) {
            return;
        }
        const remove_key = this.operatorsList.findIndex((item) => {
            return Number(item.get('opId')) === operator.opId;
        });
        if (remove_key < 0) {
            return;
        }
        const op = this.operatorsList[remove_key];

        this.eventEmitter.trigger('opLeave', op);

        this.operatorsList.splice(remove_key, 1);

        if (this.operatorsList.length > 0 && !endchat) {
            return;
        }

        // last op has left
        this.model.set({
            chatting: false,
            reconnect: false,
        });
        this.cascade.emit('signout', {});
        if (!purposeful) {
            // since the op left unexpectedly, let's do msg consolidation
            this.consolidateMsg(this.patronSession.getChannel());
        }
        this.cascade.disconnect();
        this.cleanUpDisconnect(purposeful);
    },
    /**
     * Set operator as pending.
     * @param {Object} operator Data about the operator
     */
    setOperatorPending: function (operator) {
        if (!operator.opId || operator.role !== 'op' || this.operatorsList.length === 0) {
            return;
        }
        const op = this.operatorsList.find((item) => {
            return item.get('opId') === operator.opId;
        });
        // we didn't find them or they are already away
        if (typeof op === 'undefined'
            || op.get('isAway') === true) {
            return;
        }
        op.set('isAway', true);
        // If the op doesn't return in 60 seconds we'll mark them as disconnected.
        op.setPendingTimeout(this.removeOperator.bind(this, op, false, false));
        this.eventEmitter.trigger('opAway', op);
    },
    /**
     * Set operator as pending.
     * @param {Object} operator Data about the operator
     */
    setOperatorReconnected: function (operator) {
        if (!operator.opId || operator.role !== 'op' || this.operatorsList.length === 0) {
            return;
        }
        let op = this.operatorsList.find((item) => {
            return item.get('opId') === operator.opId;
        });
        if (typeof op === 'undefined') {
            // adding a new op who returned after we thought they left.
            op = Object.create(opSession);
            op.set(operator);
            this.operatorsList.push(op);
        } else {
            // clear the timeout
            op.clearPendingTimeout();

            // in case we already marked them back.
            if (op.get('isAway') === false) {
                return;
            }
            op.set('isAway', false);
        }
        this.eventEmitter.trigger('opAway', op);
    },
    /**
     * Call the script to consolidate the transcript
     * We want to cover cases where the last op disconnected unexpectedly
     * @param {String} room The room chat is in.
     */
    consolidateMsg: function (room) {
        window.$.ajax({
            url: `/chat/session/${room}/consolidate`,
            type: 'POST',
            dataType: 'json',
            data: { iid: this.model.get('iid') },
            success: function () {},
            error: function () {},
        });
    },
    /**
     * When the chat is ended/disconnected/etc.
     * @param {Boolean} purposeful Further clarification if this was purposefully stopping the chat.
     */
    cleanUpDisconnect: function (purposeful) {
        try {
            this.deleteSessionId();
            this.model.set({
                chatting: false,
                peelingParent: false,
            });
            this.eventEmitter.trigger('endChat', purposeful);
            if (purposeful) {
                // reset disconnect tracking.
                this.disconnects = [];
            }
        } catch {
            // Ignore.
        }
    },
    /**
     * Open the (blank) window the widget will be peeled out into.
     * Must be called synchronously from the click, else popup blockers can block it.
     * @return {Window|null} New window, or null if it was blocked.
     */
    openPeelWindow: function () {
        /*
         * DON'T disconnect from chat server here - cascade will kill this socket when it is done with it
         */
        let height = 400;
        const h = this.model.get('height').toString();
        if (h.indexOf('%') === -1) {
            height = parseInt(h, 10);
        }
        let width = 375;
        const w = this.model.get('width').toString();
        if (w.indexOf('%') === -1) {
            width = parseInt(w, 10);
        }

        const newwindow = window.open(
            '',
            'name',
            `height=${height},width=${width},fullscreen=0,location=0,menubar=0,status=0`,
        );
        if (newwindow && window.focus) {
            newwindow.focus();
        }
        return newwindow;
    },
    /**
     * Activate the peel out
     *
     */
    peelOut: async function () {
        // Open the window now while we still have the click's user activation,
        // and only point it at the widget once we have the peel key.
        const newwindow = this.openPeelWindow();
        if (!newwindow) {
            // Blocked, stay in this widget.
            return;
        }
        // Get a key/hash we send in the url.
        // The resume token goes along in the body so the server can hand it to the new window,
        // which may not be able to read this widget's localStorage.
        try {
            const response = await fetch(`/chat/session/${this.patronSession.getChannel()}/peel`, {
                method: 'POST',
                body: new URLSearchParams({ token: this.resumeToken || '' }),
            });
            if (!response.ok) {
                throw new Error(`Peel key request failed: ${response.status}`);
            }
            const { peelKey = '' } = await response.json();
            if (peelKey === '') {
                throw new Error('No peel key returned');
            }
            newwindow.location.href = new URL(this.model.getPeelUrl(peelKey), window.location.href).href;
            this.model.set({
                peelingParent: true,
                reconnect: false,
            });
            this.postWindowMessage('peelout');
        } catch (err) {
            newwindow.close();
            console.error(err);
        }
    },
    /**
     * Post a message from the chat pane
     * @param {String} message Message text.
     */
    postChatMsg: async function (message) {
        let fileMsg = '';
        if (this.views.chat.filesToUpload.length > 0) {
            this.views.chat.disableForm();
            fileMsg = await this.uploadFiles();
            this.views.chat.enableForm();
        }
        message = this.model.sanitizeMessage(message) + fileMsg;
        // if we want to confirm the message was received by the server
        // this.cascade.emit('newMessage', obj, callback);
        this.cascade.emit('newMessage', { room: this.patronSession.getRoom(), message: message });
        this.eventEmitter.trigger('patronMessagePosted', message);

        this.setPatronTypingStatus(false);

        // update the saved sessionId timestamp
        this.saveSessionId();
    },
    /**
     * Send a system message to cascade for saving or showing to ops.
     * @param {String} message Message text.
     */
    postSystemMsg: function (message) {
        const m = new ChatMessage({
            role: 'system',
            room: this.patronSession.getRoom(),
            msg: this.model.sanitizeMessage(message),
        });
        this.cascade.emit('systemMessage', m);
    },
    /**
     * Function to clear typing status.
     * Called via js timeout.
     */
    timeoutTypingStatus: function () {
        this.setPatronTypingStatus(false);
    },
    /**
     * Send out a typing status for the patron
     * @param {Boolean} isTyping Typing or not.
     */
    setPatronTypingStatus: function (isTyping) {
        window.clearTimeout(this.typing_end_timeout);
        if (this.is_typing_currently === isTyping) {
            if (this.is_typing_currently) {
                // Reset the timeout.
                this.typing_end_timeout = setTimeout(this.timeoutTypingStatus.bind(this), 5000);
            }
            return;
        }
        this.is_typing_currently = isTyping;
        if (isTyping) {
            // start the timeout
            this.typing_end_timeout = setTimeout(this.timeoutTypingStatus.bind(this), 5000);
        }

        if (this.model.get('connected')) {
            const data = {
                id: this.patronSession.get('sessionId'),
                room: this.patronSession.getRoom(),
                isTyping: isTyping,
            };
            this.cascade.emit('typingStatus', data);
        }
    },
    /**
     * Patron purposefully ends the chat.
     */
    endChat: function () {
        try {
            this.deleteSessionId();
            this.model.set('reconnect', false);
            if (this.cascade && this.cascade.connected) {
                this.cascade.emit('patronChatEnd', {});
                this.cascade.disconnect();
                this.cleanUpDisconnect(true);
            } else {
                this.reloadWidget();
                this.closeChat();
            }
        } catch {
            this.eventEmitter.trigger('chatError', '', true);
        }
    },
    /**
     * When the bot resets itself after the timeout.
     */
    resetBot: function () {
        try {
            if (this.cascade && this.cascade.connected) {
                // if chatting has started don't reset...
                // just in case the timeout triggers AFTER a chat starts.
                return;
            }
            this.reloadWidget();
        } catch {
            this.eventEmitter.trigger('chatError', '', true);
        }
    },
    /**
     * Callback: message history success.
     * @param {Object} d Message data.
     */
    messageHistorySuccess: function (d) {
        this.views.chat.insertMessageHistory(d.content, d.which);
    },
    /**
     * Get messages for this chat (in case of dis/reconnect)
     * @param {String} [which=op] filter for messages either 'op' or 'all'
     */
    getMessageHistory: function (which) {
        if (typeof which === 'undefined' || ['op', 'all'].indexOf(which) === -1) {
            which = 'op';
        }
        window.$.ajax({
            url: `/chat/session/${this.patronSession.getChannel()}/messages`,
            type: 'GET',
            cache: false,
            dataType: 'json',
            data: { iid: this.model.get('iid'), which: which },
            success: this.messageHistorySuccess.bind(this),
            error: () => {}, // Ignore this error
        });
    },
    /**
     * Save a denied autopop up date
     */
    saveAutoPopDeny: function () {
        const obj = {
            date: Math.floor(Date.now() / 1000),
        };
        SuiStorage.set('libchat_auto', JSON.stringify(obj));
    },
    /**
     * Retrieve a saved autopop (if found)
     * will also delete if it is too old
     * @return {Boolean} found or not
     */
    autoPopDenied: function () {
        let obj = SuiStorage.get('libchat_auto');
        if (obj === '') {
            return false;
        }
        try {
            obj = JSON.parse(obj);
            const cur_time = Math.floor(Date.now() / 1000);
            // compare to obj.date, if it's more than 60 minutes then remove it
            if ((cur_time - obj.date) > (60 * 60)) {
                this.deleteAutoPopDeny();
                return false;
            }
            // should we update the stored timestamp here?
            return true;
        } catch {
            this.deleteAutoPopDeny();
        }
        return false;
    },
    /**
     * Delete the autopop deny
     */
    deleteAutoPopDeny: function () {
        SuiStorage.remove('libchat_auto');
    },
    /**
     * Handler: Resize chat body on window resize
     * We need this so the flexbox column works right.
     * throttled for efficiency.
     */
    setupHeightResize: function () {
        try {
            const throttle = function (type, name, obj) {
                obj = obj || window;
                let running = false;
                const func = function () {
                    if (running) {
                        return;
                    }
                    running = true;
                    requestAnimationFrame(() => {
                        obj.dispatchEvent(new CustomEvent(name));
                        running = false;
                    });
                };
                obj.addEventListener(type, func);
            };

            throttle('resize', 'optimizedResize');

            // handle event
            window.addEventListener('optimizedResize', this.adjustWidgetHeight.bind(this));
        } catch {
            // this code will not work in some browsers.
        }
    },
    /**
     * Callback: Contact success.
     */
    contactSaveSuccess: function () {
        this.views.chat.contactFormSuccess();
        // emit it to cascade so ops can get the information
        if (this.cascade.connected) {
            // Note: if any of these fields get added to, cascade must be updated
            // as it only accepts certain fields as valid to change.
            this.cascade.emit('memberUpdate', {
                sessionId: this.patronSession.get('sessionId'),
                updates: {
                    client_contact: this.patronSession.get('client_contact'),
                    name: this.patronSession.get('name'),
                    user1: this.patronSession.get('user1'),
                    user2: this.patronSession.get('user2'),
                    user3: this.patronSession.get('user3'),
                },
            });
        }
    },
    /**
     * When contact info has been saved via post-login form.
     * @param {HTMLElement} form form object.
     */
    handleContactSave: function (form) {
        window.$.ajax({
            url: form.action,
            type: 'POST',
            dataType: 'json',
            data: {
                channel: this.patronSession.getChannel(),
                client_contact: this.patronSession.get('client_contact'),
                name: this.patronSession.get('name'),
                user1: this.patronSession.get('user1'),
                user2: this.patronSession.get('user2'),
                user3: this.patronSession.get('user3'),
            },
            success: this.contactSaveSuccess.bind(this),
        }).fail(() => {
            this.views.chat.contactSaveError();
        });
    },
    /**
     * Callback: Ticket success.
     * @param {Object} data Some kind of data from script?
     */
    ticketSaveSuccess: function (data) {
        this.views.chat.showPatronMessage(data.body);
        this.views.chat.enableForm();
        this.views.chat.showChatSysMsg({
            msg: data.message,
        });
        this.model.set('ticketCreated', true);
        if (this.cascade && this.cascade.connected) {
            this.endChat();
        }
    },
    /**
     * Callback: Ticket save error.
     * @param {Object} jqXhr Jquery xhr object.
     */
    ticketSaveError: function (jqXhr) {
        this.views.chat.enableForm();
        let error = 'Sorry, an error occurred trying to create a Ticket.';
        if (jqXhr.status === 400) {
            error = jqGetAjaxError(jqXhr);
        }
        this.views.chat.showChatSysMsg({
            msg: error,
        });
    },
    /**
     * Create a Ticket from the widget
     * @param {String} message Message text.
     */
    createTicket: function (message) {
        this.views.chat.disableForm();
        window.$.ajax({
            url: `/chat/session/${this.patronSession.getChannel()}/ticket`,
            type: 'POST',
            dataType: 'json',
            data: {
                msg: message,
                iid: this.model.get('iid'),
                queue_id: this.model.get('queue_id'),
            },
            success: this.ticketSaveSuccess.bind(this),
        }).fail(this.ticketSaveError.bind(this));
    },
    /**
     * Set state to reconnecting.
     */
    setStateReconnecting: function () {
        if (this.model.hasSubscribed) {
            this.eventEmitter.trigger('chatReconnecting');
        }
    },
    /**
     * Set state to cascade errored out.
     */
    setStateCascadeError: function () {
        this.model.set('connected', false);
        if (this.model.hasSubscribed) {
            this.eventEmitter.trigger('chatError', '');
        }
    },
    /**
     * Handler: Cascade connect.
     * Connect gets called on first connect AND reconnect.
     */
    handleCascadeConnect: function () {
        let isReconnect = false;
        if (this.model.get('hasConnected') && this.patronSession.get('sessionId')) {
            isReconnect = true;
        }

        this.model.set({
            connected: true,
            reconnect: true,
            hasConnected: true,
        });

        // is this a reconnect, then we don't need to subscribe again
        if (isReconnect) {
            // gotta try to recover any missed messages
            if (this.model.get('answered')) {
                this.getMessageHistory();
            }
            if (this.model.hasSubscribed) {
                this.eventEmitter.trigger('chatReconnected');
            }
            return;
        }

        // subscribe to the main channel (starting things off) and recheck availability of ops
        this.cascade.emit('subscribe', this.patronSession.toJSON());
    },
    /**
     * Get unix timestamp in seconds.
     * @returns {Number}
     */
    getCurrentUnixSeconds: function () {
        return Math.floor(Date.now() / 1000);
    },
    /**
     * Check for excessive disconnects and suggest troubleshooting.
     * @param {Boolean} reconnectFail Was this called from a reconnect fail event
     */
    checkDisconnects: function (reconnectFail = false) {
        // Remove any that are current - threshold seconds in the past.
        const now = this.getCurrentUnixSeconds();
        const timeThreshold = this.disconnectTimeThreshold;
        this.disconnects = this.disconnects.filter((el) => {
            return (now - el) <= timeThreshold;
        });
        // Compare to threshold count.
        if (!reconnectFail && this.disconnects.length < this.disconnectCountThreshold) {
            return;
        }
        if (this.cascadeServer === this.altCascadeServer) {
            // Give up, close connection.
            this.cascade.close();
            SuiNotify.hide();
            // Show an error.
            this.setStateCascadeError();
            this.cleanUpDisconnect(false);
            return;
        }
        // set to alternate domain.
        this.cascadeServer = this.altCascadeServer;

        // Make sure current attempt is closed and that the socket gets reset to use new domain.
        // and that it is not considered a reconnect.
        this.cascade.close();
        this.cascade = null;
        // If it hasn't been answered then reset hasConnected
        // that way its not treated as a normal reconnect.
        // this avoids resetting this and then having an already answered chat get treated
        // as a new chat, which resets wait time, answered status, and can cause confusion operator side.
        if (!this.model.get('answered')) {
            this.model.set('hasConnected', false);
        }
        // Add in polling this time, just in case they don't support websockets at all.
        this.transports.unshift('polling');
        this.setupSocket();
    },
    /**
     * Handler: Cascade disconnect (purposeful or not).
     */
    handleCascadeDisconnect: function () {
        this.model.set('connected', false);
        this.cascade.io.opts.query += '&reconnect=true';
        // if we dont want to reconnect, then we should stop here.
        if (!this.model.get('reconnect')) {
            return;
        }
        this.disconnects.push(this.getCurrentUnixSeconds());
        this.checkDisconnects();
    },
    /**
     * Handler: On reconnect to the server the connection was too late and the socket had timed out.
     */
    handleCascadeReconnectRejection: function () {
        this.eventEmitter.trigger('chatError');
        this.model.set({
            chatting: false,
            reconnect: false,
        });
        this.cascade.emit('signout', {});
        this.cascade.disconnect();
        this.cleanUpDisconnect(false);
        // in some cases the working alert is still up so stop it
        SuiNotify.hide();
    },
    /**
     * Handler: When a subscribe attempt has a saved sessionId but cascade can't find the session data
     */
    handleCascadeSubscribeFail: function () {
        SuiNotify.hide();
        this.model.set('reconnect', false);
        this.cascade.disconnect();
        this.reloadWidget();
    },
    /**
     * Handler: connection/subscribe after peeling out widget.
     * @param {Object} data Cascade data object.
     * @param {Object} data.patronData Data about current user
     * @param {Array} data.ops Data about ops in conversation.
     * @param {String} [data.resumeToken] Resume token for this session, when Cascade issues a new one.
     */
    handleCascadePeelSubscribe: function (data) {
        SuiNotify.hide();
        if (data.resumeToken) {
            this.setResumeToken(data.resumeToken);
        }
        if (Array.isArray(data.ops)) {
            data.ops.forEach(function (op) {
                const opsess = Object.create(opSession);
                opsess.set(op);
                this.operatorsList.push(opsess);
                this.model.set('answered', true);
            }, this);
        }
        this.patronSession.set(data.patronData); // is this a good idea? are we resetting anything here?
        this.model.set('referer', this.patronSession.get('referer'));
        this.model.hasSubscribed = true;
        this.model.set('chatting', true);
        this.views.chat.setConnectState();

        this.getMessageHistory('all');

        // update saved session id timestamp
        this.saveSessionId();
    },
    /**
     * Handler: Subscribe sent after connect.
     * @param {Object} data Cascade data object.
     * @param {Boolean} data.online_status is the dept/user still available? 0/1/2 (offline/online/away)
     * @param {String} [data.resumeToken] Resume token for this session, when Cascade issues a new one.
     */
    handleCascadeSubscribe: function (data) {
        SuiNotify.hide();
        if (data.resumeToken) {
            this.setResumeToken(data.resumeToken);
        }
        if (data.online_status === this.model.get('status_offline')) {
            this.views.chat.showChatSysMsg({ msg: this.model.getOfflineMessage(), id: '' });
            this.model.set({
                chatting: false,
                reconnect: false,
            });
            this.cascade.emit('signout', {});
            this.cascade.disconnect();
            this.cleanUpDisconnect(false);

            // clear the missed chat timer so the message doesnt appear
            window.clearTimeout(this.views.chat.missedChatTime);
            return;
        }
        this.model.hasSubscribed = true;
        this.saveSessionId();
        this.eventEmitter.trigger('chatSubscribe', data.online_status);
    },
    /**
     * Handler: Operator has joined the chat.
     * @param {Object} data Cascade data object.
     * @param {Boolean} data.isTransfer is it a transfer
     * @param {String} data.patronId Session id for this user.
     * @param {Object} data.op The Op that joined.
     * @param {Object} data.patronUpdate Patron data that may have changed.
     * @param {Array} data.patronUpdate.ops Op ids in chat.
     * @param {Array} data.patronUpdate.transferHist Transfer history data.
     * @param {Number} data.patronUpdate.did Current chatting dept.
     * @param {Number} data.patronUpdate.uid Current chatting op owner.
     * @param {Number} data.patronUpdate.iid Current chatting site.
     * @param {Boolean} data.patronUpdate.isAnswered Chat answered.
     * @param {Object} data.patronUpdate.transferTarget Set to null.
     * @param {Array} data.patronUpdate.messages Messages (for new ops receiving this event).
     * @param {Object} data.transferHist Transfer history object (from cascade TransferHistory class).
     */
    handleOperatorJoin: function (data) {
        // save the transfer History, we may need this later.
        if (data.transferHist) {
            this.patronSession.set('transferHist', data.transferHist);
        }

        this.model.set({
            chatting: true,
            answered: true,
        });

        // save data about the op that joined
        let op;
        let isNew = false;
        if (this.operatorsList.length > 0) {
            op = this.operatorsList.find((item) => {
                return item.get('opId') === data.op.opId;
            });
        }
        if (typeof op === 'undefined') {
            // adding a new op
            op = Object.create(opSession);
            op.set(data.op);
            this.operatorsList.push(op);
            isNew = true;
        }

        // Dept greeting on first claim.
        if (this.operatorsList.length === 1) {
            if (data.patronUpdate && data.patronUpdate.did > 0) {
                const dept = this.departments[data.patronUpdate.did];
                if (dept) {
                    const deptName = dept.name.replace(/&amp;/g, '&');
                    this.eventEmitter.trigger('deptGreeting', deptName);
                }
            }
        }

        this.eventEmitter.trigger('opJoin', op, isNew);

        // For first Op
        if (this.operatorsList.length === 1) {
            const localOp = op.iid === this.model.get('iid');
            this.eventEmitter.trigger('showGreeting', localOp);
        }

        // Write the session to localStorage
        this.saveSessionId();
    },
    /**
     * Operator ends the chat purposefully.
     * @param {Object} data Cascade payload.
     * @param {Object} data.op Operator data who ended chat.
     */
    handleChatEnd: function (data) {
        const purposeful = true;
        const endchat = true;
        this.removeOperator(data.op, purposeful, endchat);
    },
    /**
     * Handler: Operator leaves chat.
     * @param {Object} data Cascade data object.
     * @param {Object} data.op info about the op who left
     */
    handleOperatorLeave: function (data) {
        const purposeful = true;
        const endchat = false;
        this.removeOperator(data.op, purposeful, endchat);
    },
    /**
     * Handler: Operator logs out/disconnects. Might be someone we aren't talking to.
     * If it is, treat it as pending... since it could be temporary or non-purposeful
     * @param {Object} member info about the removed user
     */
    handleOperatorDisconnect: function (member) {
        this.setOperatorPending(member);
    },
    /**
     * Handler: Operator logs out/disconnects not purposefully. Might be someone we aren't talking to.
     * @param {Object} member info about the removed user
     */
    handleOperatorPending: function (member) {
        this.setOperatorPending(member);
    },
    /**
     * Handler: Operator reconnects. Might be someone we aren't talking to.
     * @param {Object} member info about the removed user
     */
    handleOperatorReconnect: function (member) {
        this.setOperatorReconnected(member);
    },
    /**
     * Handler: New chat message arrives
     * @param {Object} data Cascade data object.
     */
    handleNewMessage: function (data) {
        const message = new ChatMessage(data);
        this.views.chat.showNewMessage(message);
        this.postWindowMessage('newMessage');
    },
    /**
     * Handle a delete message event from cascade.
     * @param {Number} msgId Unique message id
     */
    handleCascadeDeleteMessage: function ({ msgId = 0 }) {
        this.views.chat.removeMessage(msgId);
    },
    /**
     * Handler: Screenshare started/ended.
     * @param {Object} data Payload
     * @param {Boolean} data.isEnd Screenshare start or end.
     */
    handleScreenshareChange: function (data) {
        const event = (data.isEnd ? 'ended' : 'started');
        const message = `This screensharing session has ${event}.`;
        this.views.chat.showChatSysMsg({ msg: message, id: `screenshare_${event}` });
    },
    /**
     * Trigger a timed fallback on cascade.
     * One coop at a time.
     */
    triggerFallback: function () {
        // who is getting the fallback...
        const adjustedFallbacks = this.patronSession.get('fallbacks');
        const currentCoopId = this.patronSession.get('coopId');
        this.patronSession.removeFallbackByCoopId(currentCoopId);
        const rule = adjustedFallbacks.shift();
        if (!rule) {
            return;
        }
        this.cascade.emit('triggerFallback', { coopId: rule.c });
    },
    /**
     * Set the timer for the next fallback
     */
    setNextFallbackTimer: function () {
        const adjustedFallbacks = this.patronSession.get('fallbacks');
        const currentCoopId = this.patronSession.get('coopId');
        this.patronSession.removeFallbackByCoopId(currentCoopId);

        const rule = adjustedFallbacks.at(0);
        if (!rule || rule.fallbackSeconds <= 0) {
            return;
        }
        this.views.chat.setTimedFallbackTimeout(rule.fallbackSeconds);
    },
    /**
     * Handler: When a fallback was sent to cascade, start timer for next one
     * @param {Object} data Cascade data object.
     * @param {String} data.coopId Coop that was (or failed to be) fallen back to.
     */
    handleFallback: function ({ coopId = '' } = {}) {
        // Cascade can fall back on its own (scheduled sign-off), so make sure we don't try this coop again.
        this.patronSession.removeFallbackByCoopId(coopId);
        // if its still not answered...
        if (!this.patronSession.get('isAnswered')) {
            // try to fallback again.
            this.setNextFallbackTimer();
        }
    },
    /**
     * When patron is forcibly disconnected by someone else.
     * @param {String} who Who or why disconnect happened.
     */
    handlePatronLogOut: function ({ who = '' }) {
        // Scheduled sign-off left no one to answer the chat, so treat it like the library is offline.
        // Cascade already signed out this socket and will disconnect it, so no signout emit.
        if (who === 'scheduledSignoff') {
            this.views.chat.showChatSysMsg({ msg: this.model.getOfflineMessage(), id: '' });
            this.model.set({
                chatting: false,
                reconnect: false,
            });
            this.cleanUpDisconnect(false);
            return;
        }
        let event = 'chatError';
        const error = 'Disconnected';
        if (who === 'chatDismiss') {
            event = 'chatBlockedError';
        }
        this.model.emitter.trigger(event, error);
    },
    /**
     * Load socket.io script and trigger setup/error events.
     */
    loadSocketIo: function () {
        SuiNotify.working();
        const script = document.createElement('script');
        document.body.appendChild(script);
        script.onerror = () => {
            this.model.emitter.trigger('chatError');
        };
        script.onload = this.setupSocket.bind(this);
        script.src = this.socketIoUrl;
    },
    /**
     * Errors on the socket. Not totally sure when/what causes this to happen.
     */
    handleError: function () {
        this.setStateCascadeError();
    },
    /**
     * When a connection attempt fails.
     * Triggered each time until max attempts are reached (then you get the reconnect_failed event).
     * @param {String} err Error message. Usually something like: Error: "websocket error".
     */
    handleConnectError: function (err) {
        console.log('cascade connect_error');
        console.log(err);
        this.setStateCascadeError();
    },
    /**
     * When socket.io stops trying to connect (max attempts reached without success.)
     */
    handleReconnectFailed: function () {
        console.log('cascade reconnect_failed');
        this.setStateCascadeError();
        this.checkDisconnects(true);
    },
    /**
     * Event handlers for chat socket.
     */
    registerCascadeListeners: function () {
        /**
         * The events go like this:
         * connect
         * disconnected
         * If that was not on purpose then the following repeats:
         *
         * reconnect_attempt
         * connect_error
         * reconnect_error
         *
         * until we either succeed:
         * reconnect
         * connect
         *
         * or fail (once reconnectionAttempts have been exhausted):
         * reconnect_failed
         *
         * We don't need to have handlers for all these events else it will be an excess.
         */
        this.cascade.on('reconnect_attempt', this.setStateReconnecting.bind(this));
        this.cascade.on('connect_error', this.handleError.bind(this));
        this.cascade.io.on('error', this.handleConnectError.bind(this));
        this.cascade.io.on('reconnect_failed', this.handleReconnectFailed.bind(this));
        this.cascade.on('connect', this.handleCascadeConnect.bind(this));
        this.cascade.on('disconnect', this.handleCascadeDisconnect.bind(this));
        /**
         * On reconnect to the server the connection was too late and the socket had timed out.
         */
        this.cascade.on('reconnectReject', this.handleCascadeReconnectRejection.bind(this));
        /**
         * A connection attempt carrying a resume token was rejected (token invalid/stale).
         */
        this.cascade.on('connectReject', this.handleCascadeReconnectRejection.bind(this));
        /**
         * When a subscribe attempt has a saved sessionId but cascade can't find the session data
         */
        this.cascade.on('subscribe_failed', this.handleCascadeSubscribeFail.bind(this));
        /**
         * connection/subscribe after peeling out widget.
         */
        this.cascade.on('peel_subscribed', this.handleCascadePeelSubscribe.bind(this));
        /**
         * Handler: Subscribe post connect (send back to/from self only)
         * @param {Object} data
         * @param {Boolean} data.online_status is the dept/user still available? 0/1/2 (offline/online/away)
         */
        this.cascade.on('subscribed', this.handleCascadeSubscribe.bind(this));
        /**
         * An operator has joined the chat/room (first or subsequent)
         */
        this.cascade.on('chatJoin', this.handleOperatorJoin.bind(this));
        /**
         * typing status event
         */
        this.cascade.on('typingStatus', this.views.chat.setOperatorTypingStatus.bind(this.views.chat));
        /**
         * New message received.
         */
        this.cascade.on('newMessage', this.handleNewMessage.bind(this));
        /**
         * Op deletes an already received message.
         */
        this.cascade.on('deleteMessage', this.handleCascadeDeleteMessage.bind(this));
        /**
         * op left the chat we are in
         */
        this.cascade.on('chatLeft', this.handleOperatorLeave.bind(this));
        /**
         * op leaves chat and ends it.
         */
        this.cascade.on('chatEnd', this.handleChatEnd.bind(this));
        /**
         * Handler: user is removed from room/channel (send via the chat-{IID} room when an op disconnects) so its not necessarily someone we are chatting with
         * with shared sites the user is subscribed to other chat-{IID} rooms depending on who picks up the chat
         */
        this.cascade.on('member_removed', this.handleOperatorDisconnect.bind(this));
        /**
         * Handler: Sent via the chat-{IID} room when an op disconnects not purposefully, so its not necessarily someone we are chatting with
         */
        this.cascade.on('member_pending', this.handleOperatorPending.bind(this));
        /**
         * Handler: Sent via the chat-{IID} room when an op reconnects, so its not necessarily someone we are chatting with
         */
        this.cascade.on('member_reconnected', this.handleOperatorReconnect.bind(this));
        /**
         * Contact info request from op
         * Could do this better with arrow functions, but this will do for now.
         * passing true argument so it shows all the loging fields for this event
         */
        this.cascade.on(
            'requestContact',
            this.eventEmitter.trigger.bind(this.eventEmitter, 'requestContact', true),
        );
        this.cascade.on('screenshareChange', this.handleScreenshareChange.bind(this));
        /**
         * Handler: A fallback is offline or failed for some reason.
         */
        this.cascade.on('fallbackOffline', this.handleFallback.bind(this));
        /**
         * Handler: A fallback was online
         */
        this.cascade.on('fallbackOnline', this.handleFallback.bind(this));
        /**
         * Handler: Message when a patron is manually disconnected by someone else.
         */
        this.cascade.on('patron_log_out', this.handlePatronLogOut.bind(this));
    },
    /**
     * Start the socket connection and register all the cascade event handlers
     */
    setupSocket: function () {
        this.renderChatView();

        // reset chat ui if bot was active before...
        if (this.botController.isBotEnabled() && this.botController.isStarted) {
            this.botController.stopBot();
        }

        this.model.set('peelingParent', false);

        this.deleteAutoPopDeny();

        const params = {
            iid: this.patronSession.get('iid'),
            sessionId: this.patronSession.get('sessionId'),
            src: 'user',
            peel: this.patronSession.get('peel'),
        };
        if (this.resumeToken) {
            params.token = this.resumeToken;
        }
        // if this is the reconnect to the altserver, we need to set this, as the way its set in handleCascadeDisconnect gets cleared out.
        if (this.model.get('hasConnected') && this.model.get('answered')) {
            params.reconnect = true;
        }
        const queryString = Object.keys(params).map((key) => {
            return `${key}=${encodeURIComponent(params[key])}`;
        }).join('&');

        // Connect to the Cascade server
        // Use polling then websocket to support clients who have websockets blocked or something
        // In those cases it was not falling back to polling like expected.
        this.cascade = window.io(
            this.cascadeServer,
            {
                'force new connection': false,
                // Backoff 3s, 6s, 12s, then 15s: ~2 min of retries before reconnect_failed tries the alt domain.
                'reconnectionAttempts': 10,
                'reconnectionDelay': 3000,
                'reconnectionDelayMax': 15000,
                'timeout': 15000,
                'query': queryString,
                'transports': this.transports,
                'withCredentials': true,
            },
        );
        // don't use 'set' method here.
        this.model.cascade = this.cascade;
        this.registerCascadeListeners();
    },
    /**
     * Listen for events on the emitter.
     */
    registerEvents: function () {
        document.body.addEventListener('keydown', (ev) => {
            if (ev.key !== 'Escape') {
                return;
            }
            this.closeChat();
        });
        this.model.emitter.on('change:loggedIn', this.startSession, this);
        this.model.emitter.on('isTyping', this.setPatronTypingStatus, this);
        this.model.emitter.on('messageSubmit', this.handleMessageSubmit, this);
        this.model.emitter.on('systemMessage', this.postSystemMsg.bind(this));
        this.model.emitter.on('changeView', this.changeViewTo, this);
        this.model.emitter.on('fileUpload', this.handleFileUpload, this);
        this.model.emitter.on('contactSave', this.handleContactSave, this);
        this.model.emitter.on('startSession', this.startSession, this);
        this.model.emitter.on('captcha:verified', this.handleCaptchaVerified, this);
        this.model.emitter.on('closeWidget', this.closeChat, this);
        this.model.emitter.on('reloadWidget', this.reloadWidget, this);
        this.model.emitter.on('triggerFallback', this.triggerFallback, this);
        this.model.emitter.on('setFirstFallback', this.setNextFallbackTimer, this);
        this.eventEmitter.on('bot:startsession', this.startBotSession.bind(this));
        this.eventEmitter.on('bot:sendtochat', this.sendBotToChat.bind(this));
        this.eventEmitter.on('bot:reset', this.resetBot.bind(this));
        this.eventEmitter.on('bot:ticket:form', this.renderQuestionFormView.bind(this));
        this.eventEmitter.on('bot:booking:form', this.renderBookingFormView.bind(this));

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                // Ignore if we already started/tried chatting
                // or if this is a peeled out widget.
                // or if bot is active
                if (this.model.get('chatting')
                    || this.model.get('hasConnected')
                    || this.model.get('answered')
                    || this.model.get('loggedIn')
                    || this.model.get('peel')
                    || this.model.get('peelingParent')
                    || this.botController.isStarted) {
                    return;
                }
                // Check status, update view as/if necessary.
                this.getOnlineStatus();
            }
        });
    },
    /**
     * Add some custom nunjucks filters... (maybe someday... see ChatDashControl for examples)
     */
    initNunjucks: function () {
        this.nunjucks = new nunjucks.Environment({ autoescape: false });
        this.nunjucks.addGlobal('translation', this.model.get('translation'));
    },
    /**
     * Initialize the widget and start everything running
     * @param {Object} model chatWidget model.
     * @param {Object} patronSession model
     * @param {String} cascadeServer Url of chat server.
     * @param {String} socketIoUrl Url for socket.io.
     * @param {Object} departments Dept id/name data.
     * @param {BotFlow} flow Chat flow data.
     * @param {String} altCascadeServer Local domain url for cascade proxying.
     */
    init: function (model, patronSession, cascadeServer, socketIoUrl, departments, flow, altCascadeServer) {
        this.model = model;
        this.patronSession = patronSession;
        this.cascadeServer = cascadeServer;
        this.altCascadeServer = altCascadeServer;
        this.socketIoUrl = socketIoUrl;
        this.departments = departments;

        this.eventEmitter = new SuiEventEmitter();
        this.model.emitter = this.eventEmitter;

        this.initNunjucks();

        this.botController = new BotController(
            new BotService(this.nunjucks, flow),
            this.nunjucks,
            flow,
            this.eventEmitter,
            this.patronSession,
            this.model,
        );

        // set the body to match the iframe
        this.adjustWidgetHeight();
        this.setupHeightResize();

        // apply any user defined custom css (from widget builder)
        const css_content = this.model.get('custom_css');
        if (css_content !== '') {
            document.head.insertAdjacentHTML('beforeend', `<style>${css_content}</style>`);
        }

        // check online status now
        // this will call startWidget or renderOffline
        this.getOnlineStatus();

        // listen for events.
        this.registerEvents();
    },
};

export default chatWidgetControl;
